/**
 * A world-space panel whose entire content is one canvas texture (one draw call), with
 * rectangular hit regions resolved through the plane's UV. Redrawn only when the owner says
 * the content changed or the hovered region changed, never per frame.
 */
import * as THREE from 'three';
import type { Interactive, InteractiveSystem } from '../ui/Interactive.ts';
import type { Pointer } from '../xr/InputManager.ts';

export interface Region<T = unknown> {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  data?: T;
  /** Tooltip text lines shown on hover. */
  tooltip?: string[];
  /** Regions that do nothing on select still highlight; set false to skip highlight. */
  selectable?: boolean;
}

export interface CanvasPanelOptions {
  width: number;
  height: number;
  pixelWidth?: number;
  pixelHeight?: number;
  name?: string;
  /** Backing-store scale over the logical pixel size (1.5 = sharper text on the Quest). */
  resolution?: number;
  /** Glass: the canvas may leave its corners and margins transparent. */
  glass?: boolean;
}

/** Hover-only repaints are coalesced to this interval. */
const HOVER_REPAINT_MS = 66;
/** Leaving a region is delayed this long in case the ray jitters straight back. */
const LEAVE_DEBOUNCE_MS = 120;

export const PANEL_PX_W = 804;
export const PANEL_PX_H = 512;

export class CanvasPanel {
  readonly mesh: THREE.Mesh;
  readonly canvas = document.createElement('canvas');
  readonly ctx: CanvasRenderingContext2D;
  readonly texture: THREE.CanvasTexture;
  readonly width: number;
  readonly height: number;
  readonly pxW: number;
  readonly pxH: number;
  private regions: Region[] = [];
  private hovered: Region | undefined;
  private dirty = true;
  private hoverDirty = false;
  private lastHoverDraw = 0;
  private pendingLeave: number | undefined;
  private resolution = 1;
  private drawFn: ((ctx: CanvasRenderingContext2D, panel: CanvasPanel) => void) | undefined;
  private interactive: Interactive | undefined;
  onSelect: ((region: Region, pointer: Pointer, point: THREE.Vector3) => void) | undefined;
  onHoverChange:
    ((region: Region | undefined, point: THREE.Vector3 | undefined) => void) | undefined;
  /** Count of full redraws, for the "renders only on change" check. */
  drawCount = 0;

  constructor(options: CanvasPanelOptions) {
    this.width = options.width;
    this.height = options.height;
    this.pxW = options.pixelWidth ?? PANEL_PX_W;
    this.pxH = options.pixelHeight ?? PANEL_PX_H;
    this.ctx = this.canvas.getContext('2d')!;
    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.colorSpace = THREE.SRGBColorSpace;
    this.texture.anisotropy = 8;
    this.texture.generateMipmaps = true;
    this.texture.minFilter = THREE.LinearMipmapLinearFilter;
    this.setResolution(options.resolution ?? 1.75);
    this.mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(this.width, this.height),
      new THREE.MeshBasicMaterial({
        map: this.texture,
        toneMapped: false,
        transparent: options.glass ?? false,
        alphaTest: options.glass ? 0.02 : 0.5,
      }),
    );
    this.mesh.name = options.name ?? 'canvas-panel';
  }

  /** Changes the backing-store scale; the logical layout stays the same. */
  setResolution(resolution: number): void {
    this.resolution = resolution;
    this.canvas.width = Math.round(this.pxW * resolution);
    this.canvas.height = Math.round(this.pxH * resolution);
    this.dirty = true;
  }

  /** Registers the panel with the interactive system. */
  attach(system: InteractiveSystem): void {
    this.interactive = {
      object: this.mesh,
      touchable: true,
      onHover: (_p, point) => this.hoverAt(point),
      onLeave: () => this.hoverAt(undefined),
      onSelect: (p, point) => {
        const region = this.regionAt(point);
        if (region && this.onSelect) this.onSelect(region, p, point);
      },
    };
    system.add(this.interactive);
  }

  detach(system: InteractiveSystem): void {
    if (this.interactive) system.remove(this.interactive);
  }

  setDraw(fn: (ctx: CanvasRenderingContext2D, panel: CanvasPanel) => void): void {
    this.drawFn = fn;
    this.invalidate();
  }

  invalidate(): void {
    this.dirty = true;
  }

  /** Clears regions; the draw function registers them while drawing. */
  beginRegions(): void {
    this.regions = [];
  }

  region<T>(r: Region<T>): Region<T> {
    this.regions.push(r as Region);
    return r;
  }

  allRegions(): Region[] {
    return this.regions;
  }

  get hoveredRegion(): Region | undefined {
    return this.hovered;
  }

  isHovered(id: string): boolean {
    return this.hovered?.id === id;
  }

  /** World point to canvas pixel coordinates. */
  toPixel(worldPoint: THREE.Vector3): { x: number; y: number } {
    const local = this.mesh.worldToLocal(worldPoint.clone());
    return {
      x: (local.x / this.width + 0.5) * this.pxW,
      y: (0.5 - local.y / this.height) * this.pxH,
    };
  }

  /** Canvas pixel to world point on the panel surface. */
  toWorld(px: number, py: number, target = new THREE.Vector3()): THREE.Vector3 {
    target.set((px / this.pxW - 0.5) * this.width, (0.5 - py / this.pxH) * this.height, 0.005);
    return this.mesh.localToWorld(target);
  }

  regionAt(worldPoint: THREE.Vector3): Region | undefined {
    const { x, y } = this.toPixel(worldPoint);
    // Later regions draw on top, so search from the end.
    for (let i = this.regions.length - 1; i >= 0; i--) {
      const r = this.regions[i]!;
      if (x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h) return r;
    }
    return undefined;
  }

  private hoverAt(point: THREE.Vector3 | undefined, now = performance.now()): void {
    const region = point ? this.regionAt(point) : undefined;
    if (!region && this.hovered) {
      // The ray left every region: keep the highlight briefly in case it jitters back.
      if (this.pendingLeave === undefined) this.pendingLeave = now;
      if (now - this.pendingLeave < LEAVE_DEBOUNCE_MS) return;
    }
    this.pendingLeave = undefined;
    this.setHovered(region, point);
  }

  private setHovered(region: Region | undefined, point: THREE.Vector3 | undefined): void {
    // Regions are rebuilt on every redraw, so the same region arrives as a new object: compare
    // by id, or a resting pointer would trigger a redraw, which rebuilds regions, forever.
    if (region?.id === this.hovered?.id) {
      this.hovered = region;
      return;
    }
    this.hovered = region;
    if (region?.selectable !== false || region === undefined) this.hoverDirty = true;
    this.onHoverChange?.(region, point);
  }

  /** Redraws if anything changed. Call once per frame; cheap when clean. */
  update(now = performance.now()): void {
    if (this.pendingLeave !== undefined && now - this.pendingLeave >= LEAVE_DEBOUNCE_MS) {
      this.pendingLeave = undefined;
      this.setHovered(undefined, undefined);
    }
    if (!this.drawFn) return;
    if (!this.dirty) {
      // Hover-only repaints are rate limited; data changes repaint at once.
      if (!this.hoverDirty || now - this.lastHoverDraw < HOVER_REPAINT_MS) return;
    }
    this.dirty = false;
    this.hoverDirty = false;
    this.lastHoverDraw = now;
    this.drawCount++;
    this.beginRegions();
    this.ctx.save();
    this.ctx.setTransform(this.resolution, 0, 0, this.resolution, 0, 0);
    this.ctx.clearRect(0, 0, this.pxW, this.pxH);
    this.drawFn(this.ctx, this);
    this.ctx.restore();
    this.texture.needsUpdate = true;
  }

  dispose(): void {
    this.texture.dispose();
    (this.mesh.material as THREE.Material).dispose();
    this.mesh.geometry.dispose();
  }
}
